import React from 'react';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import BackLink from '../../../ui/BackLink';
import OldUrls from '../../../models/utils/OldUrls';
import {handleError, handleSuccess} from '../../../utils/Apollo';
import FormTitle from '../../../ui/FormTitle';
import Form from '../../../components/formConfiguration/Form';
import {AllConfigurationRulesFragment, CompleteSectionConfigurationFragment} from '../formConfiguration/fragments';
import {SectionConfigurationQL} from '../../../models/graphql/SectionConfigurationQL';
import SceneSkeleton from './SceneSkeleton';
import {IFormConfiguration} from '../../../models/form/FormConfiguration';
import {EvaluationFormContextData, FormTypeContext} from '../../../components/formConfiguration/types';

export default function AddScene() {
  const {loading, data, error} = useQuery<AddSceneData>(query, {
    errorPolicy: 'all',
  });

  const [submit] = useMutation(mutation);
  const backLink = OldUrls.evaluationFormConfigurations();

  const onFormSubmit = React.useCallback((formData) => {
    submit({
      variables: {data: formData},
    }).then(
      () => handleSuccess('Configuration added', backLink),
      (apolloError) => handleError(apolloError),
    );
  }, [submit, backLink]);

  if (error) {
    handleError(error, true);
  }

  return (
    <>
      <ReactDocumentTitle title="Add evaluation form configuration"/>
      <AppLayout className="bg-gray-light flex">
        <BackLink backLink={backLink}/>
        <FormTitle>Add evaluation form configuration</FormTitle>
        {error && <SceneSkeleton/>}
        {loading && <SceneSkeleton/>}
        {data &&
        <>
          <FormTypeContext.Provider value={EvaluationFormContextData}>
            <Form sectionConfigurations={data.sectionConfigurations} onSubmit={onFormSubmit}
              formConfiguration={data.configuration} excludeV1={true}
              backLink={backLink} withInstructions={false}/>
          </FormTypeContext.Provider>
        </>
        }
      </AppLayout>
    </>
  );
}

interface AddSceneData {
  account: {
    id: string
    __typename: string
  }
  sectionConfigurations: SectionConfigurationQL[]
  configuration: IFormConfiguration
}

const query = gql`
  query EvaluationFormAddSceneQuery {
    account {
      id
      __typename
    }

    sectionConfigurations: evaluationFormSectionConfigurations {
      ...CompleteSectionConfiguration
    }
    configuration: defaultEvaluationFormConfiguration {
      id
      rules {
        ...AllConfigurationRules
      }
      editingRights
      sections {
        id
        fields {
          id
          required
        }
      }
    }
  }
  ${AllConfigurationRulesFragment}
  ${CompleteSectionConfigurationFragment}
`;

const mutation = gql`
  mutation AddFormMutation($data: EvaluationFormConfigurationInput!) {
    createEvaluationFormConfiguration(data: $data) {
      id
      rules {
        ...AllConfigurationRules
      }
      sections {
        id
      }
    }
  }
  ${AllConfigurationRulesFragment}
`;
